// MENU COOKIE MIXIN
import {canViewTestCode} from '@/js/global/config';
import moment from 'moment-timezone';

/**
 * The cookie we store to remember a menu and cart number the customer has started but not
 * completed checking out.
 *
 * @type {string}
 */
export const MENU_COOKIE_PREFIX = 'menu';

/**
 * The number of days until the cookie expires unless it gets updated.
 *
 * @type {string}
 */
export const MENU_COOKIE_EXPIRATION = '14D';

/**
 * Get the name of the cookie stored to remember a menu and cart number the customer for which
 * the customer has started.
 *
 * @param menuProductId {number|null}
 * @return {string}
 */
export const menuCookieName = (menuProductId = null) => {
   if (typeof menuProductId !== 'number') {
      menuProductId = 'NULL';
      if(window?.mymy?.menu?.id) {
         menuProductId = window.mymy.menu.id;
      }
   }

   return [MENU_COOKIE_PREFIX, menuProductId, window.mymy.request.currentSite.country].join('_');
}

/**
 * The default settings for the menu cookie.
 *
 * @type {{p: string, q: number, d: string, n: null}}
 * @see ./src/js/funnel/_store/state.js
 * @see getMenuCookie
 */
export const MENU_COOKIE_DEFAULT_DATA = (() => {
   let q = 0;
   if(window?.mymy?.menu?.quantity?.min) {
      q = window.mymy.menu.quantity.min;
   }

   return {
      n: null, // Cart number
      q: q,    // Number of people in the customer's event
      d: '',   // Delivery date
      p: '',   // Postal Code
   };
})();

/**
 * The default settings for the availability cookie.
 *
 * @type {{p: string, q: number, d: string, n: null}}
 * @see ./src/js/funnel/_store/state.js
 * @see getAvailabilityCookie
 */
export const AVAILABILITY_COOKIE_DEFAULT_DATA = (() => {
   return {
      q: null, // Number of people in the customer's event
      d: '',   // Delivery date
      p: '',   // Postal Code
   };
})();

/**
 * Get the cookie used to associate a cart number with the current menu being viewed and other
 * data work recording.
 *
 * @type {{n: string, q: number, d: string, p: string}|null}
 * @see MENU_COOKIE_DEFAULT_DATA
 * @see setMenuCookie
 */
export const getMenuCookie = (menuProductId = null) => {
   let cookie = Cookie.get(menuCookieName(menuProductId));
   if (cookie) {
      return JSON.parse(cookie);
   }

   return null;
};

/**
 * Get the availability cookie used to set default values across menus
 *
 * @type {{n: string, q: number, d: string, p: string}|null}
 * @see AVAILABILITY_COOKIE_DEFAULT_DATA
 * @see setMenuCookie
 */
export const getAvailabilityCookie = () => {
   let cookie = Cookie.get('availability');
   if (cookie) {
      return JSON.parse(cookie);
   }

   return null;
};

/**
 * Get all "menu cookies" from the customer's browser.
 *
 * @param ignoreCountry {boolean}
 * @return {{}}
 */
export const getAllMenuCookies = (ignoreCountry = false) => {
   let menuCookies = {};
   for (const cookie of document.cookie.split(';')) {
      if (! cookie.includes(MENU_COOKIE_PREFIX + '_')) {
         continue;
      }

      let name, value;
      [name, value] = cookie.trim().split('=');

      // only get menu cookies that relate to the current site's country
      if (! ignoreCountry) {
         try {
            let country = name.split('_')[2];
            if (country !== window.mymy.request.currentSite.country) {
               continue;
            }
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
            continue;
         }
      }

      menuCookies[name] = JSON.parse(decodeURIComponent(value));
   }

   return menuCookies;
};

/**
 * @param props {{
 *    cartNumber: string,
 *    menuQuantity: number,
 *    deliveryDate: string,
 *    postalCode: string,
 * }}
 * @type {{n: string, q: number, d: string, p: string}}
 * @see MENU_COOKIE_DEFAULT_DATA
 * @see getMenuCookie
 * @see setMenuCookie in ./src/js/funnel/_store/mutations.js
 */
export const setMenuCookie = (props) => {
   // validate the argument
   if (props === null) {
      throw new Error("'props' argument is null.");
      return;
   }
   else if (typeof props !== 'object') {
      throw new Error("'props' argument is not an object.");
      return;
   }

   // check if a specific menu product ID was provided
   const menuProductId = props.menuProductId ?? null;

   // get a cookie object
   let cookie = getMenuCookie(menuProductId);
   if (! cookie) {
      cookie = {...MENU_COOKIE_DEFAULT_DATA};
   }

   // validate the cart number
   let n = props.n ?? props.cartNumber ?? null;
   if (typeof n === 'string' && n) {
      cookie.n = n;
   }

   // validate the "number of people" in the customer's event
   let q = props.q ?? props.numberOfPeople ?? 0;
   if (typeof q === 'number' && q > 0) {
      q = Math.max(q, window.mymy.menu.quantity.min);
      cookie.q = Math.min(q, window.mymy.menu.quantity.max);
   }

   // validate the delivery date
   let d = props.d ?? props.deliveryDate ?? null;
   if (typeof d === 'string' && d) {
      cookie.d = d;
   }
   else if (d instanceof Date) {
      cookie.d = moment(d).format();
   }

   // validate the postal code
   let p = props.p ?? props.postalCode ?? null;
   if (typeof p === 'string' && p) {
      cookie.p = p;
   }

   Cookie.remove(menuCookieName(menuProductId));
   Cookie.set(
      menuCookieName(menuProductId),
      JSON.stringify(cookie),
      {expires: MENU_COOKIE_EXPIRATION, secure: true}
   );

   return cookie;
};

/**
 * @param props {{
 *    menuQuantity: number,
 *    deliveryDate: string,
 *    postalCode: string,
 * }}
 * @type {{q: number, d: string, p: string}}
 * @see MENU_COOKIE_DEFAULT_DATA
 * @see getAvailabilityCookie
 * @see setAvailabilityCookie in ./src/js/funnel/_store/mutations.js
 */
export const setAvailabilityCookie = (props) => {
   // validate the argument
   if (props === null) {
      throw new Error("'props' argument is null.");
      return;
   }
   else if (typeof props !== 'object') {
      throw new Error("'props' argument is not an object.");
      return;
   }

   // get a cookie object
   let cookie = getAvailabilityCookie();
   if (! cookie) {
      cookie = {...AVAILABILITY_COOKIE_DEFAULT_DATA};
   }

   // validate the "number of people" in the customer's event
   if (typeof props.q === 'number' && props.q > 0) {
      cookie.q = props.q;
   }

   // validate the delivery date
   if (typeof props.d === 'string' && props.d) {
      cookie.d = props.d;
   }
   else if (props.d instanceof Date) {
      cookie.d = moment(props.d).format();
   }

   // validate the postal code
   if (typeof props.p === 'string' && props.p) {
      cookie.p = props.p;
   }

   Cookie.remove('availability');
   Cookie.set(
      'availability',
      JSON.stringify(cookie),
      {expires: MENU_COOKIE_EXPIRATION, secure: true}
   );

   return cookie;
};

/**
 * Delete the cookie saved for this menu.
 */
export const deleteMenuCookie = (menuProductId = null) => Cookie.remove(menuCookieName(menuProductId));


// load the functions above on the window for debugging
if (canViewTestCode()) {
   window.mymy.menuCookie = {
      name: menuCookieName(),
      expiry: MENU_COOKIE_EXPIRATION,
      get: getMenuCookie,
      set: setMenuCookie,
      delete: deleteMenuCookie,
   };
}

/**
 * Mixin for Vue components to use.
 *
 * @type {{computed: {menuCookieName(): string}, methods: {deleteMenuCookie(): *, getMenuCookie():
 *    *, setMenuCookie(*=): *}}}
 */
export const MenuCookieMixin = {
   computed: {
      menuCookieName: () => menuCookieName(),
   },
   methods: {
      /**
       * @return {*}
       * @see getMenuCookie
       */
      getMenuCookie: (menuProductId = null) => getMenuCookie(menuProductId),
      /**
       * @return {*}
       * @see getAvailabilityCookie
       */
      getAvailabilityCookie: () => getAvailabilityCookie(),
      /**
       * @return {*}
       * @see getAllMenuCookies
       */
      getAllMenuCookies: (ignoreCountry = false) => getAllMenuCookies(ignoreCountry),
      /**
       * @return {*}
       * @see setMenuCookie
       */
      setMenuCookie: props => setMenuCookie(props),
      /**
       * @return {*}
       * @see deleteMenuCookie
       */
      deleteMenuCookie: (menuProductId = null) => deleteMenuCookie(menuProductId),

      /**
       * Run this when the Vue instance mounts to check for cookie data and handle all outcomes.
       *
       * @type {object|null}
       */
      initMenuCookieCheck() {
         let menuCookie = this.getMenuCookie();
         if(!menuCookie) {
            menuCookie = this.getAvailabilityCookie();
         }

         // update the store with some of the data in the cookie
         if (menuCookie) {
            this.$store.commit('initMenuCookieStoreData', menuCookie);
            if (this.isSelectionPage) {
               this.updateFieldsWithMenuCookie(menuCookie);
            }
            if (this.canViewTestCode) {
               console.log('✅', 'Cookie "' + this.menuCookieName + '" exists:', menuCookie);
            }
         }
         else {
            if (this.canViewTestCode) {
               console.log('❌', 'No cookie set for', this.menuCookieName);
            }
         }

         return menuCookie;
      },
   },
};